import React, { useEffect, useMemo, useState } from "react";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useTheme } from "../context/ThemeContext";
import { restaurantService } from "../services";
import { RestaurantAnalyticsData } from "../utils/types";
import { formatCurrency, formatDateRange } from "../utils/analytics";

type SalesReportProps = {
  setIsSalesReportOpen: (open: boolean) => void;
};

type TabType = "delivered" | "total" | "avg";

const COLORS = ["#FF8A3D", "#7402A7", "#2196F3", "#4CAF50", "#E91E63", "#FFD6A0"];

const SalesReport = ({ setIsSalesReportOpen }: SalesReportProps) => {
  const [activeTab, setActiveTab] = useState<TabType>("delivered");
  const [filter, setFilter] = useState<"today" | "week" | "month">("month");
  const [analytics, setAnalytics] = useState<RestaurantAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response: any = await restaurantService.getAnalytics(filter, { topLimit: 8 });
        if (response.status && response.data) {
          setAnalytics(response.data);
        } else {
          setAnalytics(null);
        }
      } catch (error) {
        console.error("Failed to load sales report:", error);
        setAnalytics(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filter]);

  const trendData = useMemo(() => {
    const points = analytics?.salesTrend || [];
    return points.map((point, index) => {
      const label =
        point.label ||
        (point.date
          ? new Date(point.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
          : `P${index + 1}`);
      const value =
        activeTab === "delivered"
          ? point.orders ?? 0
          : activeTab === "avg"
            ? (point.orders ? (point.sales || point.grossSales || point.amount || 0) / point.orders : 0)
            : point.sales ?? point.grossSales ?? point.amount ?? 0;
      return { month: label, value: Number(value.toFixed(0)) };
    });
  }, [analytics, activeTab]);

  const statusData = useMemo(() => {
    const items = analytics?.orderStatus || [];
    if (items.length === 0 && analytics?.summary) {
      return [
        { name: "Delivered", value: analytics.summary.deliveredOrders, color: "#4CAF50" },
        { name: "Cancelled", value: analytics.summary.cancelledOrders, color: "#F44336" },
        {
          name: "Other",
          value: Math.max(
            0,
            analytics.summary.totalOrders -
              analytics.summary.deliveredOrders -
              analytics.summary.cancelledOrders
          ),
          color: "#FF8A3D",
        },
      ].filter((item) => item.value > 0);
    }
    return items
      .filter((item) => item.count > 0)
      .map((item, index) => ({
        name: item.status.replace(/_/g, " "),
        value: item.count,
        color: COLORS[index % COLORS.length],
      }));
  }, [analytics]);

  const paymentData = useMemo(
    () =>
      (analytics?.paymentMethods || [])
        .filter((item) => (item.amount || item.count) > 0)
        .map((item, index) => ({
          name: item.method.replace(/_/g, " "),
          value: item.amount || item.count,
          color: COLORS[index % COLORS.length],
        })),
    [analytics]
  );

  const dateRange = formatDateRange(
    analytics?.period?.startDate,
    analytics?.period?.endDate
  );

  const headline =
    activeTab === "delivered"
      ? analytics?.summary.deliveredOrders ?? 0
      : activeTab === "avg"
        ? formatCurrency(analytics?.summary.averageOrderValue ?? 0)
        : formatCurrency(analytics?.summary.grossSales ?? 0);

  const tabs = [
    { id: "delivered" as TabType, label: "Delivered Orders" },
    { id: "total" as TabType, label: "Total Sales" },
    { id: "avg" as TabType, label: "Avg Order Value" },
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300 overflow-y-auto"
      onClick={() => setIsSalesReportOpen(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-900 shadow-2xl rounded-l-[20px] overflow-y-auto flex flex-col"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 z-10 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={arrowLeft}
                alt="back"
                className="w-[36px] h-[36px] rounded-full border cursor-pointer p-2"
                onClick={() => setIsSalesReportOpen(false)}
              />
              <h2 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[20px] font-semibold">
                Sales Report
              </h2>
            </div>
            <img src={cancel} alt="close" className="w-6 h-6 cursor-pointer" onClick={() => setIsSalesReportOpen(false)} />
          </div>
        </div>

        <div className="flex-1 px-6 py-4">
          <div className="flex gap-2 mb-4">
            {(["today", "week", "month"] as const).map((item) => (
              <button
                key={item}
                onClick={() => setFilter(item)}
                className={`px-3 py-1 rounded-full text-sm border ${
                  filter === item ? "bg-[#FF8A00] text-white border-[#FF8A00]" : "border-gray-300"
                }`}
              >
                {item === "today" ? "Today" : item === "week" ? "This week" : "This month"}
              </button>
            ))}
          </div>

          {dateRange && (
            <p className="text-[#FF6B2C] text-sm mb-3">{dateRange}</p>
          )}

          <div className="flex gap-2 mb-6 border-b dark:border-gray-700">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2 text-[13px] font-Urbanist font-medium ${
                  activeTab === tab.id
                    ? "text-[#FF6B35] border-b-2 border-[#FF6B35]"
                    : "text-[#050505] dark:text-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8A00]"></div>
            </div>
          ) : !analytics ? (
            <p className="text-center text-gray-500 py-16">No sales data available</p>
          ) : (
            <>
              <p className="text-[#FF8A00] font-Urbanist text-[24px] font-semibold mb-4">{headline}</p>
              <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg p-2">
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={trendData} margin={{ left: 0, right: 10, top: 10, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? "#374151" : "#f0f0f0"} />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: isDarkMode ? "#9CA3AF" : "#706B66", fontSize: 11 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: isDarkMode ? "#9CA3AF" : "#706B66", fontSize: 12 }} />
                      <Bar dataKey="value" fill="#FF8A3D" radius={[4, 4, 0, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-sm text-gray-500 py-8">No trend data for this period</p>
                )}
              </div>

              <PieBlock title="Order status" dateRange={dateRange} data={statusData} />
              <PieBlock title="Payment methods" dateRange={dateRange} data={paymentData} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PieBlock = ({
  title,
  dateRange,
  data,
}: {
  title: string;
  dateRange: string;
  data: { name: string; value: number; color: string }[];
}) => (
  <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg p-4">
    <h3 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[18px] font-semibold">{title}</h3>
    {dateRange && <p className="text-[#FF6B2C] text-xs mb-3">for {dateRange}</p>}
    {data.length === 0 ? (
      <p className="text-sm text-gray-500 py-6">No data</p>
    ) : (
      <div className="flex items-center gap-4 w-full">
        <ResponsiveContainer width={180} height={180}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
              {data.map((entry, index) => (
                <Cell key={entry.name + index} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-2.5">
          {data.map((item) => (
            <div key={item.name} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
              <span className="text-[#706B66] dark:text-gray-400 text-sm capitalize">
                {item.value} {item.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default SalesReport;
